import {LisansEntity} from "@/entity/LisansEntity";
import {PathToProduct, Product} from "@/enum/Product";
import {daysLeftUntil} from "@/utils/renewalOffer";

/**
 * DEV-2113 — ürün bazlı lisans durumu.
 *
 * Sunucudaki ValidateLicenseMiddleware ile AYNI dört durumu üretir. Ekran tarafı
 * bu durumu yalnızca GÖSTERMEK için kullanır; engelleme sunucuda yapılır. İkisi
 * ayrışırsa kullanıcı "yapabilirim" sanıp 403 yer, ya da tersi.
 *
 *   gecerli     end_date >= bugün
 *   kisitli     dolmuş, üzerinden kısıt süresinden az geçmiş  -> matris uygulanır
 *               ⚠ YALNIZ KADEMELI_URUNLER bu kademeyi yaşar; e-İcraPro yaşamaz
 *   sure_bitti  dolmuş, üzerinden kısıt süresi kadar geçmiş   -> ürün tamamen kapalı
 *   yok         o ürüne ait hiç lisans satırı yok             -> ürün tamamen kapalı
 *
 * Kısıt süresi (gün) sunucudan gelir (`/ben` -> `lisans_kisit_gun`); buraya
 * KOPYALANMAZ. Değer gelmezse VARSAYILAN_KISIT_GUN kullanılır — sunucu eski
 * sürümdeyse ekran yine de çalışsın diye.
 */
export type LisansDurum = "gecerli" | "kisitli" | "sure_bitti" | "yok";

/** Sunucu değeri gelmediğinde kullanılan yedek. config/license.php ile aynı. */
export const VARSAYILAN_KISIT_GUN = 90;

/**
 * Kademeli (kısıtlı) dönemi OLAN ürünler — 17.09.2026 ürün kararı.
 *
 * e-İcraPro bu listede YOK: süresi dolduğu an kapanır, ara kademe yaşamaz.
 * Ürün sahibinin ifadesiyle: "e-İcraPro için yetkiye gerek yok, lisansı yoksa
 * direk kapansın." e-İcraPro ayrı bir yazılımdır; OfisPro/İcraPro'daki "var olan
 * dosyayla çalışmaya devam et" ihtiyacı orada yoktur.
 *
 * ⚠ Bu, kısıt SÜRESİNDEN ayrı bir karardır ve bu yüzden burada duruyor: süre
 * (gün) sunucudan gelir çünkü ortam ayarıyla değişebilir; "hangi ürün kademe
 * yaşar" ise ürün kuralıdır ve burada durur.
 * Sunucudaki karşılığı: config/license.php -> grace_products.
 * İkisi ayrışırsa SUNUCU kazanır; kullanıcı 403 alıp lisans uyarısını görür.
 */
export const KADEMELI_URUNLER: number[] = [Product.OFISPRO, Product.ICRAPROWEB];

/** İki tarih arasındaki tam gün farkı; saat/dakika sayılmaz (sunucu da gün başına yuvarlıyor). */
export function gecenGunSayisi(bitis: string | Date, simdi: Date = new Date()): number {
    const b = new Date(bitis);
    const bitisGun = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    const bugun = Date.UTC(simdi.getFullYear(), simdi.getMonth(), simdi.getDate());
    return Math.round((bugun - bitisGun) / 86400000);
}

/** Ürünün EN GÜNCEL lisans satırı. Sunucu ürün başına tek satır gönderir; yine de sona bakılır. */
export function urununLisansi(
    lisanslar: LisansEntity[],
    urun: Product | PathToProduct
): LisansEntity | undefined {
    return [...(lisanslar ?? [])]
        .reverse()
        .find((l: LisansEntity) => l.product_id == urun);
}

export function lisansDurumu(
    lisanslar: LisansEntity[],
    urun: Product | PathToProduct,
    kisitGun: number = VARSAYILAN_KISIT_GUN,
    simdi: Date = new Date()
): LisansDurum {
    const lisans = urununLisansi(lisanslar, urun);
    if (!lisans) return "yok";

    const gecen = gecenGunSayisi(lisans.end_date, simdi);
    if (gecen <= 0) return "gecerli";        // bitiş bugün veya ileride

    // Kademeyi yalnız KADEMELI_URUNLER yaşar; diğerinde sınır 0 demektir,
    // yani lisans dolar dolmaz "sure_bitti".
    const sinir = KADEMELI_URUNLER.includes(Number(urun)) ? Math.max(0, kisitGun) : 0;
    return gecen < sinir ? "kisitli" : "sure_bitti";
}

/** Kısıtlı dönemde kaç gün kaldı (uyarı metni için). Kısıtlı değilse null. */
export function kisitliKalanGun(
    lisanslar: LisansEntity[],
    urun: Product | PathToProduct,
    kisitGun: number = VARSAYILAN_KISIT_GUN,
    simdi: Date = new Date()
): number | null {
    if (lisansDurumu(lisanslar, urun, kisitGun, simdi) !== "kisitli") return null;

    const lisans = urununLisansi(lisanslar, urun)!;
    return Math.max(0, Math.max(0, kisitGun) - gecenGunSayisi(lisans.end_date, simdi));
}

/**
 * Kullanıcı bu ürünün ekranlarına GİREBİLİR mi?
 *
 * Geçerli ve kısıtlı dönemde girer (kısıtlı dönemde çalışmaya devam etmesi
 * talebin ta kendisi). Süre bittiğinde ve lisans hiç yokken giremez -- sunucu
 * zaten her ucu kapatır, boş ekranda hata yağmuru yerine lisans ekranına gider.
 */
export function urunEkranlariAcikMi(
    lisanslar: LisansEntity[],
    urun: Product | PathToProduct,
    kisitGun: number = VARSAYILAN_KISIT_GUN,
    simdi: Date = new Date()
): boolean {
    const durum = lisansDurumu(lisanslar, urun, kisitGun, simdi);
    return durum === "gecerli" || durum === "kisitli";
}

/**
 * Bir urunun yenileme yolu.
 *
 * IKI SINIR OLCULDU, ikisi de sunucudan:
 *
 * 1. URUN — `RenewalController::resolveProductId` YALNIZ 'icrapro' ve 'eicrapro'
 *    taniyor ve tanimadigi degeri SESSIZCE IcraProWeb'e dusuruyor. OfisPro'yu o
 *    uca yollamak kullaniciyi YANLIS URUN icin sepete dusururdu -- OfisPro
 *    bugun de magaza baglantisina gider.
 *
 * 2. PENCERE — `RenewalOfferService::mintCheckoutUrl` yalnizca UST siniri
 *    uyguluyor: kalan gun 30'dan fazlaysa reddediyor. ALT SINIR YOK; suresi
 *    dolmus lisans da sepete gidiyor (DEV-2113 ile kaldirildi -- bu talebin
 *    hedef kitlesi zaten suresi dolmus kullanici).
 *
 * ⚠ Kampanya indirimi bitis ONCESINE ait; suresi dolmus kullanici sepete
 * gider ama indirim almaz. Karar sunucuda veriliyor, ekran karismaz.
 */
export type YenilemeHedefi =
    | { tur: "self-servis"; product: "icrapro" | "eicrapro" }
    | { tur: "baglanti"; url: string };

export const MAGAZA_URL = "https://zirve-bilgisayar.com/";

/** Sunucudaki RenewalOfferService::OFFER_WINDOW_DAYS ile ayni. */
export const YENILEME_PENCERESI_GUN = 30;

/** Urunun self-servis akistaki karsiligi; OfisPro'nun karsiligi YOK. */
function selfServisUrunAdi(urun: Product | PathToProduct): "icrapro" | "eicrapro" | null {
    if (urun == Product.ICRAPROWEB) return "icrapro";
    if (urun == Product.EICRAPRO) return "eicrapro";
    return null;
}

export function yenilemeHedefi(
    lisanslar: LisansEntity[],
    urun: Product | PathToProduct,
    simdi: Date = new Date()
): YenilemeHedefi {
    const ad = selfServisUrunAdi(urun);
    if (!ad) return {tur: "baglanti", url: MAGAZA_URL};

    const lisans = urununLisansi(lisanslar, urun);
    if (!lisans) return {tur: "baglanti", url: MAGAZA_URL};

    // Sunucunun kabul ettigi pencere: kalan <= 30 (alt sinir yok).
    // tierForDaysLeft BILEREK kullanilmiyor -- o, DEV-3099'un teklif
    // kademelerini (buton/modal/geri sayim) tarif ediyor ve negatif gunde null
    // donuyor; yenileme penceresiyle artik ayni sey degil.
    const kalan = daysLeftUntil(lisans.end_date, simdi);
    return kalan <= YENILEME_PENCERESI_GUN
        ? {tur: "self-servis", product: ad}
        : {tur: "baglanti", url: MAGAZA_URL};
}

/** Bu urun icin yenileme eylemi gosterilmeli mi? Yalniz gecerli olanlarda gerekmez. */
export function yenilemeGerekliMi(
    lisanslar: LisansEntity[],
    urun: Product | PathToProduct,
    kisitGun: number = VARSAYILAN_KISIT_GUN,
    simdi: Date = new Date()
): boolean {
    return lisansDurumu(lisanslar, urun, kisitGun, simdi) !== "gecerli";
}

/**
 * Ekranın lisans kısıtıyla bir düğmeyi sorduğu işlem anahtarları.
 *
 * Hangi anahtarın o büro için kapalı olduğuna SUNUCU karar verir: /ben
 * yanıtındaki `ozellik_durumu.kapali_islemler` (DEV-2113 Faz 4). Anahtarın hangi
 * ürüne bağlı olduğu ve büroya verilen istisnalar sunucudaki merkez tabloda
 * durur. Önceden burada anahtar -> ürün tablosu (ISLEM_URUNU) vardı; büro
 * istisnasını bilemediği için kaldırıldı.
 *
 * Her anahtar kısıt matrisindeki BİR satıra karşılık gelir; genel bir kural
 * ("ekleme kapalıdır" gibi) BİLEREK yok: matris düzgün değil -- büroda ekleme
 * kapalı düzenleme açık, raporda ekleme AÇIK çıktı kapalı.
 *
 * Ekran bunu yalnızca GÖSTERMEK için kullanır (buton pasif + sebep); engelleme
 * sunucuda yapılır.
 */
export type KisitliIslem =
    | "buro-ekle"
    | "buro-sil"
    | "personel-yaz"
    | "kullanici-izinleri"
    | "vekalet-ekle"
    | "vekalet-sil"
    | "rapor-alma"
    | "dilekce-indirme"
    | "uyari-olustur"
    | "takip-ac"
    | "takip-not"
    | "takip-taksit"
    | "takip-dokuman"
    | "takip-ek-alan"
    | "tercih-yaz"
    | "toplu-xml"
    | "tebligat-yaz"
    | "haciz-yaz"
    | "satis-yaz";

/** /ben -> ozellik_durumu (sunucu: OzellikDurumuOzeti). */
export interface OzellikDurumu {
    /** Ürün id -> dört durumdan biri. */
    urun_durumlari: Record<string, LisansDurum>;
    /** Bu büroda şu an kapalı işlem anahtarları. */
    kapali_islemler: string[];
    /**
     * Özellik anahtarı -> bu büroda açık mı. Listede olmayan anahtar açıktır (varsayılan
     * açık; sunucunun özellik kapısı da satırı olmayan özelliği açık sayar).
     */
    ozellikler: Record<string, boolean>;
    /** Özetin bayatlayacağı ilk an (ISO 8601); değişecek bir şey yoksa null. */
    sonraki_gecis: string | null;
}

/**
 * Bu işlem lisans yüzünden kapalı mı?
 *
 * Özet yoksa (sunucu üretemediyse) kapalı SAYILMAZ: düğme açık görünür, işlemi
 * sunucu yine engeller ve kullanıcı lisans uyarısını görür. Ekranda her şeyi
 * kilitlemek programı kullanılamaz hale getirirdi.
 */
export function islemKisitliMi(
    ozellikDurumu: OzellikDurumu | null | undefined,
    islem: KisitliIslem | string
): boolean {
    return !!ozellikDurumu?.kapali_islemler?.includes(islem);
}

/** Butonun ucundaki açıklama. Kapalı değilse boş string (idiom: :tooltip=""). */
export function islemKisitSebebi(
    ozellikDurumu: OzellikDurumu | null | undefined,
    islem: KisitliIslem | string
): string {
    return islemKisitliMi(ozellikDurumu, islem)
        ? "Kullanım süreniz doldu, kısıtlı modda bu işlem yapılamıyor."
        : "";
}

/**
 * DEV-2113 — ekranda gösterilecek ETKİN lisans durumu.
 *
 * Taban ürün kuralı (OfisPro, üst ürün geçerli/kısıtlıysa onu miras alır)
 * SUNUCUDA tanımlıdır: config/license.php -> taban_urun_bagimliligi ve tek
 * yardımcı UrunLisansDurumu::etkinDurum. Ekran bu kuralı YENİDEN HESAPLAMAZ —
 * /ben özetinin `urun_durumlari` alanından okur (tek kanonik kaynak, DEMİR
 * KURAL #11). İki yerde ayrı yazılsaydı biri açar, diğeri "süreniz doldu" derdi.
 *
 * Özet yoksa (eski sunucu ya da özet üretilememiş) `licenses[]` end_date'ten
 * hesaplanan ham duruma DÜŞER — eski davranış korunur, ekran yine çalışır.
 */
export function etkinLisansDurumu(
    ozellikDurumu: OzellikDurumu | null | undefined,
    lisanslar: LisansEntity[],
    urun: Product | PathToProduct,
    kisitGun: number = VARSAYILAN_KISIT_GUN,
    simdi: Date = new Date()
): LisansDurum {
    const sunucu = ozellikDurumu?.urun_durumlari?.[String(urun)];
    if (sunucu === "gecerli" || sunucu === "kisitli" || sunucu === "sure_bitti" || sunucu === "yok") {
        return sunucu;
    }
    return lisansDurumu(lisanslar, urun, kisitGun, simdi);
}

/**
 * Kullanıcı bu ürünün ekranlarına GİREBİLİR mi — ETKİN duruma göre.
 * urunEkranlariAcikMi ile aynı kural (geçerli ya da kısıtlı girer), ama taban
 * ürün mirasını içeren etkin durumu kullanır: üst ürün geçerliyken OfisPro
 * ekranları da açılır.
 */
export function etkinUrunEkranlariAcikMi(
    ozellikDurumu: OzellikDurumu | null | undefined,
    lisanslar: LisansEntity[],
    urun: Product | PathToProduct,
    kisitGun: number = VARSAYILAN_KISIT_GUN,
    simdi: Date = new Date()
): boolean {
    const durum = etkinLisansDurumu(ozellikDurumu, lisanslar, urun, kisitGun, simdi);
    return durum === "gecerli" || durum === "kisitli";
}

/**
 * Bu özellik bu büroda açık mı? Sunucuda 'ozellik:{anahtar}' kapısının arkasında
 * duran bir özellik, ekranda göstermeden önce buna sorulur. Kapalıysa menüde
 * görünmez; adresine gidilirse "Bu özellik büronuzda aktif değil" sayfası açılır.
 *
 * Soru "bu büroda kapalı mı"dır: yalnız açıkça kapatılmış özellik kapalıdır.
 * VARSAYILAN AÇIK (26.09.2026 kararı): yeni özellik kimseyi kısıtlamadan gelir,
 * kısıtlamak ekip kararıdır. Özet yoksa da açık sayılır (sunucu kapısı da kararı
 * hesaplayamazsa isteği geçirir).
 */
export function ozellikAcikMi(ozellikDurumu: OzellikDurumu | null | undefined, anahtar: string): boolean {
    const harita = ozellikDurumu?.ozellikler;
    if (!harita || !Object.prototype.hasOwnProperty.call(harita, anahtar)) return true;
    return harita[anahtar] !== false;
}

/**
 * Rotanın bağlı olduğu özellik: rota kaydında `meta.ozellik`. İç içe rotada en
 * dıştaki kayıttan başlanır (vue-router `to.matched` sırası); özellik yoksa null.
 */
export function rotaOzelligi(metalar: Array<Record<string, unknown> | null | undefined>): string | null {
    for (const meta of metalar) {
        const ozellik = meta?.ozellik;
        if (typeof ozellik === "string" && ozellik !== "") return ozellik;
    }
    return null;
}

/**
 * /ben yeniden çekilmeli mi? Alan hiç yoksa (deploy öncesi açılmış oturum), özet
 * eski biçimdeyse (özellik haritası yok) ya da `sonraki_gecis` geçmişse EVET. Sunucu
 * özeti üretemediyse (null) HAYIR: her sayfa geçişinde aynı hatayı yeniden istemek
 * bir şey değiştirmez.
 */
export function ozetBayatMi(ozellikDurumu: OzellikDurumu | null | undefined, simdi: Date = new Date()): boolean {
    if (ozellikDurumu === undefined) return true;
    if (ozellikDurumu === null) return false;
    // Özellik haritası sonradan geldi; eski özetle açık özellik kapalı görünürdü.
    if (ozellikDurumu.ozellikler === undefined) return true;
    if (!ozellikDurumu.sonraki_gecis) return false;
    const an = new Date(ozellikDurumu.sonraki_gecis).getTime();
    return !isNaN(an) && an <= simdi.getTime();
}

/**
 * Zamanlayıcının alt sınırı. Tarayıcı saati sunucudan ilerideyse geçiş anı hep
 * "geçmiş" görünür; alt sınır olmasaydı ekran /ben'i durmadan çekerdi. Geçişler
 * gece yarısı olduğu için birkaç dakikalık gecikme zararsızdır; engellemeyi
 * zaten sunucu yapar.
 */
export const TAZELEME_ALT_SINIR_MS = 5 * 60 * 1000;

/** Üst sınır: tarayıcı zamanlayıcısı ~24,8 günden uzağını kuramaz; en geç günde bir tazelenir. */
export const TAZELEME_UST_SINIR_MS = 24 * 60 * 60 * 1000;

/**
 * Lisans engelinde (403, kapalı görünen ürün ekranı, lisans ekranının açılışı)
 * /ben en fazla bu sıklıkta çekilir: engel art arda tekrarlansa da tek istek gider.
 */
export const ENGEL_TAZELEME_ALT_SINIR_MS = 30 * 1000;

/**
 * Ürün ekranları kapalı görünüyorsa karar eldeki özetle verilmeden önce özet
 * tazelenir. Oturumdaki özet, lisans yenilendikten sonra da "süresi doldu" demeye
 * devam ediyordu: bitmiş ürünün sonraki geçiş anı yok, kullanıcı içeri giremediği
 * için 403 de gelmiyor; yenileyen kullanıcı aynı sekmede bölüme hiç giremiyordu.
 * Tazeleme başarısız olursa eldeki özetle karar verilir.
 */
export async function ekranAcikMiTazeleyerek(
    acikMi: () => boolean,
    tazele: () => Promise<unknown>
): Promise<boolean> {
    if (acikMi()) return true;
    try {
        await tazele();
    } catch (e) {
        // Ağ ya da /ben hatası: eldeki özetle karar verilir.
    }
    return acikMi();
}

/** `sonraki_gecis` anına kadar beklenecek süre (ms), sınırlar içinde; kurulacak zamanlayıcı yoksa null. */
export function tazelemeGecikmesi(ozellikDurumu: OzellikDurumu | null | undefined, simdi: Date = new Date()): number | null {
    if (!ozellikDurumu?.sonraki_gecis) return null;
    const an = new Date(ozellikDurumu.sonraki_gecis).getTime();
    if (isNaN(an)) return null;
    return Math.min(Math.max(an - simdi.getTime(), TAZELEME_ALT_SINIR_MS), TAZELEME_UST_SINIR_MS);
}

/**
 * Suresi dolmus urunler icin surekli uyari metni.
 *
 * Talebin 1. maddesinin ek notu: "Kullaniciya SUREKLI olarak '3 ay sonra tum
 * alanlar engellenecektir' uyarisi gosterilecek." Metin gun sayisiyla uretilir
 * cunku kullanicinin bilmesi gereken sey sabit "3 ay" degil, KENDI kalan
 * suresidir.
 *
 * SURE_BITTI de bara GIRER. Ilk surumde girmiyordu, gerekce "o urunde zaten
 * lisans ekrani cikiyor" idi -- ama bu yalniz kullanici O URUNE girmeye
 * calisirsa dogru. OfisPro'su gecerli, IcraPro'su bitmis bir kullanici gun
 * boyu OfisPro'da calisir ve IcraPro'sunun tamamen kapandigini HIC gormez.
 * Kalici bar tam olarak bunun icin var.
 *
 * Lisansi hic olmayan (durum "yok") urun bara girmez: satin alinmamis bir urun
 * icin "süreniz doldu" demek yanlis olur.
 */
export function kisitBarMesaji(
    urunler: Array<{ad: string; kalan: number | null; durum?: LisansDurum}>
): string | null {
    const kisitli = urunler.filter((u) => u.kalan !== null);
    const bitmis = urunler.filter((u) => u.durum === "sure_bitti");

    if (kisitli.length === 0 && bitmis.length === 0) return null;

    const cumleler: string[] = [];

    if (kisitli.length > 0) {
        const parcalar = kisitli.map((u) => `${u.ad}: ${u.kalan} gün`);
        cumleler.push(`Kullanım süreniz doldu, şu an kısıtlı modda çalışıyorsunuz. ${parcalar.join(", ")} sonra kısıtlı mod sona erecek.`);
    }

    if (bitmis.length > 0) {
        const adlar = bitmis.map((u) => u.ad).join(", ");
        cumleler.push(`${adlar} için kısıtlı mod sona erdi; kullanmaya devam etmek için süre uzatmanız gerekiyor.`);
    }

    return cumleler.join(" ");
}

/**
 * Yenileme eyleminin ETIKETI.
 *
 * Lisansi HIC olmayan urunde "Uzat" yanlis: uzatilacak bir sey yok, kullanici
 * o urunu ilk kez alacak. Talebin dili de "paket satin al" -- etiket duruma
 * gore degisir ki dugme ne yapacagini dogru soylesin.
 */
export function yenilemeEtiketi(durum: LisansDurum): string {
    return durum === "yok" ? "Paket satın al" : "Süreyi uzat";
}
